import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";

import { googleApi } from "../../../services/api/jtl/google";
import { JTLCredentialsApi } from "../../../services/api/jtl/jtl_credentials";
import { sheetsSettingsApi } from "../../../services/api/jtl/sheets";
import { JtlApi, InboundMonitoringApi } from "../../../services/api/jtl/jtl";
import Cron from "react-cron-generator";
import "react-cron-generator/build/cron-builder.css";
import { useTheme } from "@mui/material/styles";
import CronBuilderBox from "../../../components/CronBuilderBox";
import { getMutedPanelSx } from "../../../theme/panelStyles";

const getDateMinusDaysDDMMYYYY = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const InboundsCreationTab = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Manual inbound states
  const [manualInboundDate, setManualInboundDate] = useState(
    getDateMinusDaysDDMMYYYY(1),
  );
  const [manualInboundLoading, setManualInboundLoading] = useState(false);
  const [manualInboundResult, setManualInboundResult] = useState(null);

  // Cron settings
  const [globalSettings, setGlobalSettings] = useState({
    cron_expression: "0 */30 * * * *",
    is_cron_active: false,
    last_run: null,
    next_run: null,
  });

  // System status states
  const [googleCredentials, setGoogleCredentials] = useState([]);
  const [sheetsSettings, setSheetsSettings] = useState({});
  const [columnSettings, setColumnSettings] = useState({});
  const [JTLCredentials, setJTLCredentials] = useState([]);

  useEffect(() => {
    loadCronStatus();
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    try {
      setLoading(true);
      const [googleCreds, jtlCreds, sheetSettings] = await Promise.all([
        googleApi.getCredentials(),
        JTLCredentialsApi.getCredentials(),
        sheetsSettingsApi.getSettings(),
      ]);

      setGoogleCredentials(googleCreds.data || []);
      setJTLCredentials(jtlCreds.data || []);

      if (sheetSettings.data) {
        setSheetsSettings({
          spreadsheetId: sheetSettings.data.spreadsheet_id,
        });
        setColumnSettings({
          dateColumn: sheetSettings.data.date_column,
          orderPlaceColumn: sheetSettings.data.order_place_column,
          unitColumn: sheetSettings.data.unit_column,
          asinSkuColumn: sheetSettings.data.asin_sku_column,
          sendStatusColumn: sheetSettings.data.send_status_column,
          confirmationColumn: sheetSettings.data.confirmation_column,
        });
      }
    } catch (error) {
      console.error("Failed to load system data:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to load system data",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleManualInbound = async () => {
    try {
      setManualInboundLoading(true);
      setError(null);
      setManualInboundResult(null);
      const response = await JtlApi.create_inbounds(
        manualInboundDate,
        4,
        localStorage.getItem("token"),
      );
      const result = response.data;
      const successMessage =
        result.error_items && result.error_items.length > 0
          ? `Inbound created with ${result.error_items.length} items failed. Check logs for details.`
          : "Manual inbound created successfully!";
      setSuccess(successMessage);
      setManualInboundResult({
        success: true,
        message: successMessage,
        data: result,
      });
    } catch (error) {
      console.error("Failed to create manual inbound:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to create manual inbound";
      setManualInboundResult({
        success: false,
        message: errorMessage,
      });
      setError(errorMessage);
    } finally {
      setManualInboundLoading(false);
    }
  };

  const loadCronStatus = async () => {
    try {
      const response = await InboundMonitoringApi.getCronStatus();
      const data = response.data;
      setGlobalSettings((prev) => ({
        ...prev,
        is_cron_active: data.active,
        cron_expression: data.expression || prev.cron_expression,
        last_run: data.last_run_at,
        next_run: data.next_run_at,
      }));
    } catch (error) {
      console.error("Failed to load cron status:", error);
    }
  };

  const toggleMonitoring = async () => {
    try {
      setLoading(true);
      if (globalSettings.is_cron_active) {
        await InboundMonitoringApi.stopCronMonitor();
        setGlobalSettings((prev) => ({ ...prev, is_cron_active: false }));
        setSuccess("Cron monitoring stopped successfully!");
      } else {
        await InboundMonitoringApi.startCronMonitor(
          globalSettings.cron_expression,
        );
        setGlobalSettings((prev) => ({ ...prev, is_cron_active: true }));
        setSuccess(
          `Cron monitoring started with schedule: ${globalSettings.cron_expression}`,
        );
      }
      loadCronStatus();
    } catch (error) {
      setError("Failed to toggle cron monitoring: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateCronSchedule = async () => {
    try {
      setLoading(true);
      await InboundMonitoringApi.startCronMonitor(
        globalSettings.cron_expression,
      );
      setSuccess("Cron schedule updated successfully!");
      loadCronStatus();
    } catch (error) {
      setError("Failed to update cron schedule: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkSystemStatus = () => {
    const hasActiveCredential = googleCredentials.some(
      (cred) => cred.active_inbound_checker && cred.status === "authorized",
    );
    const hasValidSheetSettings =
      sheetsSettings.spreadsheetId &&
      (columnSettings.dateColumn ||
        columnSettings.unitColumn ||
        columnSettings.asinSkuColumn ||
        columnSettings.sendStatusColumn ||
        columnSettings.orderPlaceColumn ||
        columnSettings.confirmationColumn);
    const hasActiveJTLCredential = JTLCredentials.some((cred) => cred.active);
    return {
      hasActiveCredential,
      hasValidSheetSettings,
      hasActiveJTLCredential,
      isReady:
        hasActiveCredential && hasValidSheetSettings && hasActiveJTLCredential,
    };
  };

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Inbound Center
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  This service runs automatically at the configured cron
                  schedule and creates inbounds in JTL for the previous day,
                  based on the current system configuration.
                </Typography>
              </Alert>
              <List>
                <ListItem>
                  <ListItemIcon>
                    {checkSystemStatus().hasActiveCredential ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Google Credentials"
                    secondary={
                      checkSystemStatus().hasActiveCredential
                        ? "Active and authorized credential found"
                        : "No active or authorized credentials"
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {checkSystemStatus().hasValidSheetSettings ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="Sheets Settings"
                    secondary={
                      checkSystemStatus().hasValidSheetSettings
                        ? "Valid sheet configuration found"
                        : "Sheet settings incomplete"
                    }
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    {checkSystemStatus().hasActiveJTLCredential ? (
                      <CheckCircleIcon sx={{ color: "success.main" }} />
                    ) : (
                      <ErrorIcon sx={{ color: "error.main" }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary="JTL Credentials"
                    secondary={
                      checkSystemStatus().hasActiveJTLCredential
                        ? "Active and authorized credential found"
                        : "No active or authorized credentials"
                    }
                  />
                </ListItem>
              </List>

              {checkSystemStatus().isReady ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ✅ System is ready for Inbound process!
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ⚠️ Please complete the configuration in other tabs before
                    starting Inbound process.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Manual Inbound Creation Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Manual Inbound Creation
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You can manually trigger inbound creation for a specific date.
                  If no date is provided, yesterday will be used by default.
                </Typography>
              </Alert>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <TextField
                  label="Date (DD-MM-YYYY)"
                  type="date"
                  value={manualInboundDate || ""}
                  onChange={(e) => setManualInboundDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 180 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleManualInbound}
                  disabled={
                    manualInboundLoading || !checkSystemStatus().isReady
                  }
                  startIcon={
                    manualInboundLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : null
                  }
                >
                  {manualInboundLoading
                    ? "Creating..."
                    : "Start Manual Inbound"}
                </Button>
              </Box>
              {manualInboundResult && manualInboundResult.message && (
                <Alert
                  severity={manualInboundResult.success ? "success" : "error"}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2">
                    {manualInboundResult.message}
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={12}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">Inbound Control</Typography>
              </Box>
              <Grid container spacing={2}>
                {/* Cron Schedule Section */}
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: "medium" }}
                  >
                    Automated Inbound Schedule
                  </Typography>
                  <CronBuilderBox
                    mb={2}
                    sx={{
                      "& .cron_builder .row": { marginBottom: "15px" },
                      "& .cron_builder select, & .cron_builder input": {
                        padding: "4px 8px",
                      },
                    }}
                  >
                    <Cron
                      onChange={(e) =>
                        setGlobalSettings((prev) => ({
                          ...prev,
                          cron_expression: e,
                        }))
                      }
                      value={globalSettings.cron_expression}
                      showResultText={true}
                      showResultCron={true}
                      locale="en"
                    />
                  </CronBuilderBox>
                  <Box display="flex" alignItems="flex-start" gap={1} mt={2}>
                    <Button
                      variant={
                        globalSettings.is_cron_active ? "outlined" : "contained"
                      }
                      color={
                        globalSettings.is_cron_active ? "error" : "success"
                      }
                      startIcon={
                        globalSettings.is_cron_active ? (
                          <StopIcon />
                        ) : (
                          <PlayArrowIcon />
                        )
                      }
                      onClick={toggleMonitoring}
                      disabled={
                        loading ||
                        (!globalSettings.is_cron_active &&
                          !checkSystemStatus().isReady)
                      }
                      size="small"
                      sx={{ minWidth: "120px" }}
                    >
                      {globalSettings.is_cron_active ? "Stop" : "Start"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={updateCronSchedule}
                      disabled={loading || !globalSettings.is_cron_active}
                      size="small"
                      sx={{ minWidth: "100px" }}
                    >
                      Update
                    </Button>
                    {loading && <CircularProgress size={20} />}
                  </Box>
                  <Box sx={getMutedPanelSx(theme)}>
                    <Typography variant="subtitle2" gutterBottom>
                      Status
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong>{" "}
                      {globalSettings.is_cron_active ? (
                        <span style={{ color: "green" }}>Active</span>
                      ) : (
                        <span style={{ color: "red" }}>Inactive</span>
                      )}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Current Cron:</strong>{" "}
                      {globalSettings.cron_expression || "N/A"}
                    </Typography>
                    {globalSettings.next_run && (
                      <Typography variant="body2">
                        <strong>Next Run:</strong>{" "}
                        {globalSettings.is_cron_active
                          ? new Date(globalSettings.next_run).toLocaleString()
                          : "-"}
                      </Typography>
                    )}
                    {globalSettings.last_run && (
                      <Typography variant="body2">
                        <strong>Last Run:</strong>{" "}
                        {new Date(globalSettings.last_run).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
export default InboundsCreationTab;
